import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Lightbulb } from "lucide-react";
import ilajlyLogo from "@/assets/ilajly-logo-icon.png";

const HEALTH_TIPS = [
  "💧 Drinking warm water first thing in the morning aids digestion.",
  "🧘 Deep breathing for 2 minutes can reduce stress significantly.",
  "🌿 Homoeopathy treats the person, not just the disease.",
  "😴 7-8 hours of quality sleep boosts your immune system.",
  "🥗 Eating seasonal fruits strengthens natural immunity.",
  "🚶 A 30-minute daily walk can prevent chronic diseases.",
  "☀️ Morning sunlight helps your body produce Vitamin D.",
  "🍵 Herbal teas can soothe inflammation naturally.",
  "📖 Keeping a health diary helps track your wellness journey.",
  "🧠 Mental health is as important as physical health.",
  "🫁 Practice pranayama daily for better respiratory health.",
  "💤 Avoid screens 30 minutes before sleep for better rest.",
];

interface UpdateSplashScreenProps {
  visible: boolean;
  stage: string;
  progress: number;
  success: boolean;
}

export function UpdateSplashScreen({ visible, stage, progress, success }: UpdateSplashScreenProps) {
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * HEALTH_TIPS.length));

  // Rotate tips every 4 seconds
  useEffect(() => {
    if (!visible || success) return;
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % HEALTH_TIPS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [visible, success]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background"
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="mb-8"
          >
            <img
              src={ilajlyLogo}
              alt="ilajly"
              className="w-24 h-24 object-contain"
            />
          </motion.div>

          {/* Progress section */}
          <div className="w-72 space-y-4">
            {!success ? (
              <>
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-muted-foreground text-center">
                  {stage || "Updating…"}
                </p>

                {/* Health tip */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={tipIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 px-4 py-3 rounded-xl bg-muted/50 border border-border"
                  >
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {HEALTH_TIPS[tipIndex]}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </>
            ) : (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="flex flex-col items-center gap-3"
              >
                <CheckCircle className="w-12 h-12 text-primary" />
                <p className="text-sm font-medium text-foreground">Updated successfully!</p>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
